import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'info'
    | 'success';
  onConfirm: () => void;
  onCancel: () => void;
  icon?: ReactNode;
}

/**
 * Reusable confirmation dialog component
 * Displays a modal with a title, message, and confirm/cancel buttons
 * Optional icon displayed centered above the title
 */
export function ConfirmationDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  onConfirm,
  onCancel,
  icon,
}: ConfirmationDialogProps) {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      onClick={(e) => e.stopPropagation()}
      PaperProps={
        icon
          ? {
              sx: {
                border: `2px solid ${alpha(theme.palette.info.main, 0.6)}`,
                textAlign: 'center',
              },
            }
          : undefined
      }
    >
      {icon && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3, pb: 0 }}>
          {icon}
        </Box>
      )}
      <DialogTitle
        sx={
          icon ? { textAlign: 'center', textTransform: 'uppercase' } : undefined
        }
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={
            icon
              ? { textAlign: 'center', textTransform: 'uppercase' }
              : undefined
          }
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={icon ? { justifyContent: 'space-around', pb: 3 } : undefined}
      >
        {icon ? (
          <>
            <Button onClick={onConfirm} color={confirmColor} autoFocus>
              {confirmLabel}
            </Button>
            <Button onClick={onCancel} color={confirmColor}>
              {cancelLabel}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onCancel} color={confirmColor}>
              {cancelLabel}
            </Button>
            <Button onClick={onConfirm} color={confirmColor} autoFocus>
              {confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
